import {
  ASSIGNABLE_ROLES,
  memberPermissionsPath,
  type TeamInvitationItem,
  type TeamMemberItem,
} from '@fos/shared/contracts/team';
import {
  INVITATION_STATUS_LABELS,
  INVITATION_STATUS_TONES,
  MEMBERSHIP_STATUS_LABELS,
  MEMBERSHIP_STATUS_TONES,
  ROLE_LABELS,
} from '@fos/shared/labels';
import {
  ChevronDown,
  MailPlus,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  Users,
  UserX,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatDateShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { Table, TBody, Td, THead, Th, Tr } from '../../../ui/Table';
import { toast } from '../../../ui/Toast';
import { MobileFab, TOUCH_ICON_END, useIsPhone } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import { RoleMatrix } from './components/RoleMatrix';
import { InviteDialog } from './InviteDialog';
import { canEditRole, formatAiLimit, initialsFromEmail, toastMutationError } from './team-ui';

type Confirm =
  | { kind: 'deactivate'; member: TeamMemberItem }
  | { kind: 'cancel'; invitation: TeamInvitationItem }
  | null;

/**
 * Ustawienia → Zespół (Figma 3034:4305): osoby i zaproszenia (rola, klienci, limit AI, status), zmiana roli
 * z listy, menu „…” (uprawnienia, wyłączenie dostępu, ponowne wysłanie / anulowanie zaproszenia), „Role i dostęp”.
 * Manager widzi listę bez zarządzania; Użytkownik — informację, że zespołem zarządza administrator.
 */
export function TeamSettingsPage() {
  const session = useActiveSession();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const team = trpc.team.overview.useQuery();
  const [inviteOpen, setInviteOpen] = useState(false);
  const phone = useIsPhone();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const refresh = () => void utils.team.overview.invalidate();

  const updateRole = trpc.team.updateRole.useMutation({
    onSuccess: (m) => {
      toast.success('Rola zmieniona', { description: `${m.name}: ${ROLE_LABELS[m.role]}` });
      refresh();
    },
    onError: toastMutationError,
  });
  const deactivate = trpc.team.deactivate.useMutation({
    onSuccess: (r) => {
      toast.success('Dostęp wyłączony', { description: `${r.member.name}: wylogowano z tego workspace’u.` });
      refresh();
    },
    onError: toastMutationError,
    onSettled: () => setConfirm(null),
  });
  const reactivate = trpc.team.reactivate.useMutation({
    onSuccess: (r) => {
      toast.success('Dostęp włączony', { description: `${r.member.name}: można się znowu zalogować.` });
      refresh();
    },
    onError: toastMutationError,
  });
  const resend = trpc.team.resendInvitation.useMutation({
    onSuccess: (inv) => {
      toast.success('Zaproszenie wysłane ponownie', { description: `Nowy link do ${inv.email} jest ważny 7 dni.` });
      refresh();
    },
    onError: toastMutationError,
  });
  const cancel = trpc.team.cancelInvitation.useMutation({
    onSuccess: () => {
      toast.success('Zaproszenie anulowane');
      refresh();
    },
    onError: toastMutationError,
    onSettled: () => setConfirm(null),
  });

  const forbidden = team.isError && appErrorCode(team.error) === 'FORBIDDEN';
  const canManage = team.data?.canManage ?? false;
  const actorRole = session.membership?.role ?? null;

  return (
    <div>
      <SettingsHeader
        title="Zespół"
        description={
          canManage
            ? 'Każda osoba ma swoich klientów, zadania, czas i akceptacje. Jako admin widzisz wszystko.'
            : 'Osoby w workspace i ich role. Zespołem zarządza administrator.'
        }
        actions={
          canManage ? (
            <Button variant="primary" icon={Plus} onClick={() => setInviteOpen(true)} className="max-md:hidden">
              Zaproś osobę
            </Button>
          ) : undefined
        }
      />
      {canManage && phone && (
        <MobileFab icon={Plus} onClick={() => setInviteOpen(true)}>
          Zaproś osobę
        </MobileFab>
      )}

      <Section className="mt-6">
        {team.isPending ? (
          <ListSkeleton rows={3} />
        ) : forbidden ? (
          <EmptyState
            icon={Users}
            title="Zespołem zarządza administrator"
            description="Listę osób i ich uprawnienia widzą administratorzy i managerowie."
          />
        ) : team.isError ? (
          <ErrorState onRetry={() => void team.refetch()} retrying={team.isFetching} />
        ) : team.data.members.length === 0 && team.data.invitations.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nikogo jeszcze nie ma w zespole"
            action={
              canManage ? (
                <Button variant="primary" icon={Plus} onClick={() => setInviteOpen(true)}>
                  Zaproś osobę
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table aria-label="Członkowie zespołu" className="table-fixed md:min-w-[640px] md:table-auto">
            <THead>
              <Tr>
                <Th>Osoba</Th>
                <Th className="hidden md:table-cell w-[150px]">Rola</Th>
                <Th align="right" className="hidden md:table-cell w-[70px]">
                  Klienci
                </Th>
                <Th align="right" className="hidden md:table-cell w-[90px]">
                  Limit AI
                </Th>
                <Th className="hidden md:table-cell w-[170px] pl-4">Status</Th>
                <Th className="w-10">
                  <span className="sr-only">Akcje</span>
                </Th>
              </Tr>
            </THead>
            <TBody>
              {team.data.members.map((m) => (
                <Tr key={m.membershipId}>
                  {/* telefon: link z nazwą rozciągnięty na całą komórkę osoby (pole dotyku = wiersz bez „⋯”) */}
                  <Td className="max-md:relative">
                    <div className="flex items-center gap-3">
                      <Avatar initials={m.initials} color={m.avatarColor} size={32} />
                      <div className="min-w-0">
                        <p className="flex items-baseline gap-2 truncate font-semibold text-fg">
                          {canManage ? (
                            <Link
                              to={memberPermissionsPath(m.membershipId)}
                              className="truncate rounded-xs hover:underline focus-visible:outline-2 focus-visible:outline-accent max-md:after:absolute max-md:after:inset-0 max-md:after:content-['']"
                            >
                              {m.name}
                            </Link>
                          ) : (
                            <span className="truncate">{m.name}</span>
                          )}
                          {m.isSelf && <span className="text-caption font-normal text-fg-muted">Ty</span>}
                        </p>
                        <p className="truncate text-caption text-fg-muted">{m.email}</p>
                        <p className="truncate text-caption text-fg-secondary md:hidden">
                          {ROLE_LABELS[m.role]} · {MEMBERSHIP_STATUS_LABELS[m.status]}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td className="hidden md:table-cell">
                    {canManage && canEditRole(m, actorRole) && m.status === 'active' ? (
                      <Menu>
                        <MenuTrigger
                          aria-label={`Rola: ${ROLE_LABELS[m.role]}. Zmień rolę osoby ${m.name}`}
                          className="-ml-1.5 inline-flex h-8 items-center gap-1 rounded-md px-1.5 text-fg-secondary hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
                        >
                          {ROLE_LABELS[m.role]}
                          <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
                        </MenuTrigger>
                        <MenuContent>
                          <MenuRadioGroup
                            value={m.role}
                            onValueChange={(role) =>
                              updateRole.mutate({
                                membershipId: m.membershipId,
                                role: role as (typeof ASSIGNABLE_ROLES)[number],
                                version: m.version,
                              })
                            }
                          >
                            {[...ASSIGNABLE_ROLES].reverse().map((role) => (
                              <MenuRadioItem key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </MenuRadioItem>
                            ))}
                          </MenuRadioGroup>
                        </MenuContent>
                      </Menu>
                    ) : (
                      <span className="text-fg-secondary">{ROLE_LABELS[m.role]}</span>
                    )}
                  </Td>
                  <Td align="right" className="hidden md:table-cell">
                    {m.clientCount === null ? (
                      <span className="text-caption text-fg-muted">wszyscy</span>
                    ) : (
                      <Mono>{m.clientCount}</Mono>
                    )}
                  </Td>
                  <Td align="right" className="hidden md:table-cell">
                    <Mono className={m.aiMonthlyLimitPln ? undefined : 'text-fg-muted'}>
                      {formatAiLimit(m.aiMonthlyLimitPln)}
                    </Mono>
                  </Td>
                  <Td className="hidden md:table-cell pl-4">
                    <StatusPill tone={MEMBERSHIP_STATUS_TONES[m.status]}>
                      {MEMBERSHIP_STATUS_LABELS[m.status]}
                    </StatusPill>
                  </Td>
                  <Td align="right">
                    {canManage && (m.role !== 'owner' || actorRole === 'owner') && (
                      <Menu>
                        <MenuTrigger
                          aria-label={`Akcje: ${m.name}`}
                          className={`relative inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_ICON_END}`}
                        >
                          <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden />
                        </MenuTrigger>
                        <MenuContent align="end">
                          <MenuItem icon={ShieldCheck} onSelect={() => navigate(memberPermissionsPath(m.membershipId))}>
                            Uprawnienia
                          </MenuItem>
                          {!m.isSelf && m.role !== 'owner' && (
                            <>
                              <MenuSeparator />
                              {m.status === 'active' ? (
                                <MenuItem
                                  icon={UserX}
                                  tone="danger"
                                  onSelect={() => setConfirm({ kind: 'deactivate', member: m })}
                                >
                                  Wyłącz dostęp
                                </MenuItem>
                              ) : (
                                <MenuItem
                                  icon={UserCheck}
                                  onSelect={() =>
                                    reactivate.mutate({ membershipId: m.membershipId, version: m.version })
                                  }
                                >
                                  Włącz dostęp
                                </MenuItem>
                              )}
                            </>
                          )}
                        </MenuContent>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))}
              {team.data.invitations.map((inv) => (
                <Tr key={inv.id}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <Avatar initials={initialsFromEmail(inv.email)} size={32} />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-fg">{inv.email}</p>
                        <p className="truncate text-caption text-fg-muted">
                          {inv.status === 'pending'
                            ? `Ważne do ${formatDateShort(inv.expiresAt)}`
                            : `Wygasło ${formatDateShort(inv.expiresAt)}`}
                          {inv.invitedBy ? ` · zaprasza ${inv.invitedBy.name}` : ''}
                        </p>
                        <p className="truncate text-caption text-fg-secondary md:hidden">
                          {ROLE_LABELS[inv.role]} · {INVITATION_STATUS_LABELS[inv.status]}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td className="hidden md:table-cell text-fg-secondary">{ROLE_LABELS[inv.role]}</Td>
                  <Td align="right" className="hidden md:table-cell">
                    {inv.clientCount === null ? (
                      <span className="text-caption text-fg-muted">wszyscy</span>
                    ) : (
                      <Mono>{inv.clientCount}</Mono>
                    )}
                  </Td>
                  <Td align="right" className="hidden md:table-cell">
                    <Mono className={inv.aiMonthlyLimitPln ? undefined : 'text-fg-muted'}>
                      {formatAiLimit(inv.aiMonthlyLimitPln)}
                    </Mono>
                  </Td>
                  <Td className="hidden md:table-cell pl-4">
                    <StatusPill tone={INVITATION_STATUS_TONES[inv.status]}>
                      {INVITATION_STATUS_LABELS[inv.status]}
                    </StatusPill>
                  </Td>
                  <Td align="right">
                    {canManage && (
                      <Menu>
                        <MenuTrigger
                          aria-label={`Akcje zaproszenia: ${inv.email}`}
                          className={`relative inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_ICON_END}`}
                        >
                          <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden />
                        </MenuTrigger>
                        <MenuContent align="end">
                          <MenuItem
                            icon={inv.status === 'pending' ? RefreshCw : MailPlus}
                            onSelect={() => resend.mutate({ invitationId: inv.id })}
                          >
                            Wyślij ponownie
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem
                            icon={XCircle}
                            tone="danger"
                            onSelect={() => setConfirm({ kind: 'cancel', invitation: inv })}
                          >
                            Anuluj zaproszenie
                          </MenuItem>
                        </MenuContent>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Section>

      {!forbidden && <RoleMatrix />}

      {canManage && <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />}

      <Dialog
        open={confirm?.kind === 'deactivate'}
        onOpenChange={(open) => !open && setConfirm(null)}
        width="s"
        title={confirm?.kind === 'deactivate' ? `Wyłączyć dostęp: ${confirm.member.name}?` : 'Wyłączyć dostęp?'}
        description="Osoba zostanie od razu wylogowana z tego workspace’u, a jej tokeny API przestaną działać. Zadania i wpisy czasu zostają. Dostęp możesz włączyć ponownie."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              icon={UserX}
              loading={deactivate.isPending}
              onClick={() =>
                confirm?.kind === 'deactivate' &&
                deactivate.mutate({ membershipId: confirm.member.membershipId, version: confirm.member.version })
              }
            >
              Wyłącz dostęp
            </Button>
          </>
        }
      />
      <Dialog
        open={confirm?.kind === 'cancel'}
        onOpenChange={(open) => !open && setConfirm(null)}
        width="s"
        title="Anulować zaproszenie?"
        description={
          confirm?.kind === 'cancel'
            ? `Link wysłany do ${confirm.invitation.email} przestanie działać.`
            : 'Link z zaproszenia przestanie działać.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Wróć
            </Button>
            <Button
              variant="primary"
              loading={cancel.isPending}
              onClick={() => confirm?.kind === 'cancel' && cancel.mutate({ invitationId: confirm.invitation.id })}
            >
              Anuluj zaproszenie
            </Button>
          </>
        }
      />
    </div>
  );
}
